import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { useSession } from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb'
import { PageContainer } from 'components/layout/PageContainer'
import { PageContent } from 'components/layout/PageContent'
import theme from 'config/theme'
import { useListaRegulacaoQuery, useVisualizarTodasNotificacoesByTipoTopicoMutation } from 'graphql/hooks.generated'
import { ListaRegulacaoQueryInput, TipoTopicoNotificacaoEnum } from 'graphql/types.generated'
import { useCallback, useMemo, useState } from 'react'
import { metaPath } from 'util/metaPath'

import { ListaRegulacaoFilter } from './components/ListaRegulacaoFilter'
import { ListaRegulacaoTable } from './components/ListaRegulacaoTable'
import {
  LISTA_REGULACAO_DEFAULT_FILTERS,
  ListaRegulacaoFilterModel,
  ListaRegulacaoTableFilterModel,
} from './model-regulacao'

const path = metaPath<ListaRegulacaoTableFilterModel>()

export const RegulacaoView = () => {
  const { data: sessao } = useSession()
  const { acesso } = useAcessoLotacaoOrEstagio()

  const initialFilter: ListaRegulacaoFilterModel = useMemo(
    () => ({
      ...LISTA_REGULACAO_DEFAULT_FILTERS,
      tableFilter: {
        ...LISTA_REGULACAO_DEFAULT_FILTERS.tableFilter,
        unidadeSaude: acesso.unidadeSaude,
        equipe: acesso.equipe,
      },
    }),
    [acesso.equipe, acesso.unidadeSaude]
  )

  const [filter, setFilter] = useState(initialFilter)
  const [visualizarTodasNotificacoesByTipoTopico] = useVisualizarTodasNotificacoesByTipoTopicoMutation({
    variables: { tipoTopico: TipoTopicoNotificacaoEnum.REGULACAO_ASSISTENCIAL },
  })

  const { data, loading, refetch } = useListaRegulacaoQuery({
    variables: {
      input: filtersToVariablesConverter(filter, sessao.acesso.id),
    },
    onCompleted: () => {
      visualizarTodasNotificacoesByTipoTopico()
    },
  })

  const updateFilter = useCallback((newFilter: ListaRegulacaoFilterModel) => setFilter(newFilter), [setFilter])

  useCallback(
    (newFilter: ListaRegulacaoFilterModel) => {
      if (newFilter !== filter) {
        refetch()
        visualizarTodasNotificacoesByTipoTopico({
          variables: { tipoTopico: TipoTopicoNotificacaoEnum.REGULACAO_ASSISTENCIAL },
        })
      }
    },
    [filter, refetch, visualizarTodasNotificacoesByTipoTopico]
  )

  return (
    <>
      <Breadcrumb title='Regulação assistencial' />
      <PageContent fluid type='transparent' style={{ paddingTop: '1.5rem' }}>
        <PageContainer fluid style={{ backgroundColor: theme.pallete.surface.main, padding: '0rem 1rem 1rem 1rem' }}>
          <ListaRegulacaoFilter path={path} filter={filter} updateFilter={updateFilter} />
          <ListaRegulacaoTable data={data} loading={loading} filter={filter} updateFilter={updateFilter} />
        </PageContainer>
      </PageContent>
    </>
  )
}

const filtersToVariablesConverter = (
  filter: ListaRegulacaoFilterModel,
  acessoLotacaoId: string
): ListaRegulacaoQueryInput => {
  const lotacoesIds = filter.tableFilter.isSomenteMeusAtendimentos
    ? [acessoLotacaoId]
    : filter.tableFilter.solicitantes?.map((solicitante) => solicitante.id)

  return {
    filtro: filter.tableFilter.nomeCnsCpf,
    tipoRegulacao: filter.tableFilter.tipoRegulacao ?? [],
    statusRegulacao: filter.tableFilter.statusRegulacao ?? [],
    equipeIne: filter.tableFilter.equipe?.ine,
    unidadeSaudeCnes: filter.tableFilter.unidadeSaude?.cnes,
    ordem: filter.tableFilter.ordem,
    lotacoesIds: lotacoesIds,
    periodo: filter.tableFilter.periodo,
    somenteNaoLidos: filter.tableFilter.isSomenteNaoLidos,
    pageParams: filter.pageParams,
  }
}
